import { Suspense } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'

import PageLoader from '@/shared/components/PageLoader'
import { AuthLayout } from '@/providers'

import ErrorPage from '@/pages/ErrorPage'
import ParentLogin from '@/pages/ParentLogin'
import Unauthorized from '@/pages/unauthorized/Unauthorized'
import AccountLayout from '@/pages/account/AccountLayout'
import Signup from '@/pages/account/signup/Signup'
import ResetPassword from '@/pages/account/reset/ResetPassword'
import UserLayout from '@/pages/user/UserLayout'
import UserIndex from '@/pages/user/UserIndex'
import Dashboard from '@/pages/user/dashboard/Dashboard'
import Settings from '@/pages/user/settings/Settings'
import ParentIndex from '@/pages/user/parent/ParentIndex'
import AlternativeContact from '@/pages/user/parent/alternative-contact/AlternativeContact'
import CoParent from '@/pages/user/parent/coparent/CoParent'
import ChildrenList from '@/pages/user/parent/children-list/ChildrenList'
import EditChild from '@/pages/user/parent/children-list/[id]/EditChild'
import MyDuplicates from '@/pages/user/parent/duplicates/MyDuplicates'
import TeacherLayout from '@/pages/user/teacher/TeacherLayout'
import TeacherIndex from '@/pages/user/teacher/TeacherIndex'
import CheckInOut from '@/pages/user/teacher/check-in-out/CheckInOut'
import ParentRegistry from '@/pages/user/teacher/parents/ParentRegistry'
import TeacherRegistry from '@/pages/user/teacher/teachers/TeacherRegistry'
import DuplicateRegistration from '@/pages/user/teacher/duplicates/DuplicateRegistration'
import Queue from '@/pages/user/teacher/queue/Queue'
import TeacherReport from '@/pages/user/teacher/teacher-report/TeacherReport'
import AdminLayout from '@/pages/admin/AdminLayout'
import AdminHome from '@/pages/admin/AdminHome'
import AdminResourceRoute from '@/pages/admin/AdminResourceRoute'

export const router = createBrowserRouter([
    {
        element: <AuthLayout />,
        errorElement: <ErrorPage />,
        children: [
            { path: '/', element: <ParentLogin /> },
            { path: '/unauthorized', element: <Unauthorized /> },
            {
                path: '/account',
                element: <AccountLayout />,
                children: [
                    { path: 'signup', element: <Signup /> },
                    { path: 'reset', element: <ResetPassword /> }
                ]
            },
            {
                path: '/user',
                element: <UserLayout />,
                children: [
                    { index: true, element: <UserIndex /> },
                    { path: 'dashboard', element: <Dashboard /> },
                    { path: 'settings', element: <Settings /> },
                    {
                        path: 'parent',
                        children: [
                            { index: true, element: <ParentIndex /> },
                            {
                                path: 'alternative-contact',
                                element: <AlternativeContact />
                            },
                            {
                                // Only useSuspenseQuery caller in the app — Suspense ancestor required
                                path: 'coparent',
                                element: (
                                    <Suspense fallback={<PageLoader />}>
                                        <CoParent />
                                    </Suspense>
                                )
                            },
                            { path: 'children-list', element: <ChildrenList /> },
                            { path: 'children-list/:id', element: <EditChild /> },
                            { path: 'duplicates', element: <MyDuplicates /> }
                        ]
                    },
                    {
                        path: 'teacher',
                        element: <TeacherLayout />,
                        children: [
                            { index: true, element: <TeacherIndex /> },
                            { path: 'check-in-out', element: <CheckInOut /> },
                            { path: 'parents', element: <ParentRegistry /> },
                            { path: 'teachers', element: <TeacherRegistry /> },
                            {
                                path: 'duplicate-registration',
                                element: <DuplicateRegistration />
                            },
                            {
                                path: 'logs',
                                element: <Navigate to="/user/teacher/parents" replace />
                            },
                            {
                                // Pending approvals now live in the teacher
                                // registry behind its "Pending" status filter.
                                path: 'pending-teachers',
                                element: (
                                    <Navigate
                                        to="/user/teacher/teachers?status=pending"
                                        replace
                                    />
                                )
                            },
                            { path: 'queue', element: <Queue /> },
                            { path: 'teacher-report', element: <TeacherReport /> }
                        ]
                    }
                ]
            },
            {
                path: '/admin',
                element: <AdminLayout />,
                children: [
                    { index: true, element: <AdminHome /> },
                    { path: ':resource', element: <AdminResourceRoute /> }
                ]
            }
        ]
    }
])
